import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaArrowLeft, FaUpload, FaPercentage } from "react-icons/fa";
import moment from "moment";
import { ApiHandler } from "../../helper/ApiHandler";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../components/Loading";

/* ================= INITIAL STATE ================= */
const initialState = {
    impressions: "",
    click: "",
    revenue: "",
    fill_rate: "",
    ctr: "",
    cpm: "",
    viewability: "",
    viewable_imps: "",
};

/* ================= HELPERS ================= */
const toInt = (v) =>
    v === "" || v === null || v === undefined ? "" : Math.floor(Number(v));

const AddRevenuePage = () => {
    const { userId, domainId } = useParams();
    const { userToken } = useSelector((state) => state.user);
    const navigate = useNavigate();
    const lastValidData = React.useRef(null);

    const dispatch = useDispatch();

    const [date, setDate] = useState(new Date());
    const [formData, setFormData] = useState(initialState);

    const [baseData, setBaseData] = useState({
        impressions: "",
        click: "",
        revenue: "",
    });

    const [percentage, setPercentage] = useState("");
    const [loading, setLoading] = useState(true);

    /* ================= FETCH OLD DATA ================= */
    useEffect(() => {
        if (userToken && userId && domainId && date) {
            fetchDashboardData();
        }
        // eslint-disable-next-line
    }, [userToken, userId, domainId, date]);

    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const response = await ApiHandler(
                "/get_revenue_data.php",
                "POST",
                {
                    page: 0,
                    perpage: 1,
                    user_id: userId,
                    domain_id: domainId,
                    date: moment(date).format("YYYY-MM-DD"),
                },
                userToken,
                dispatch,
                navigate
            );

            if (
                response?.data?.status === "1" &&
                response.data.data &&
                response.data.data.length > 0
            ) {
                const d = response.data.data[0];

                const data = {
                    impressions: toInt(d.impressions),
                    click: toInt(d.click),
                    revenue: d.revenue || "",
                    fill_rate: d.fill_rate || "",
                    ctr: d.ctr || "",
                    cpm: d.cpm || "",
                    viewability: d.viewability || "",
                    viewable_imps: toInt(d.viewable_imps),
                };

                // Save last valid API data
                lastValidData.current = data;

                setFormData(data);
                setBaseData({
                    impressions: data.impressions,
                    click: data.click,
                    revenue: data.revenue,
                });
            } else {
                // API empty → show previous date data
                if (lastValidData.current) {
                    setFormData(lastValidData.current);
                    setBaseData({
                        impressions: lastValidData.current.impressions,
                        click: lastValidData.current.click,
                        revenue: lastValidData.current.revenue,
                    });
                } else {
                    setFormData(initialState);
                    setBaseData({ impressions: "", click: "", revenue: "" });
                }
            }
        } catch {
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    };


    /* ================= CSV UPLOAD (OLD) ================= */
    const handleCSVUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const fd = new FormData();
        fd.append("csv_file", file);

        try {
            const res = await fetch("https://api.myad360.com/csv/upload.php", {
                method: "POST",
                body: fd,
            });

            const result = await res.json();
            applyCSVData(result);
        } catch {
            toast.error("CSV upload error");
        }
    };

    /* ================= CSV UPLOAD 2 (NEW) ================= */
    const handleCSVUpload2 = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const fd = new FormData();
        fd.append("csv_file", file);

        try {
            const res = await fetch(
                "https://api.myad360.com/csv/upload_2.php",
                {
                    method: "POST",
                    body: fd,
                }
            );

            const result = await res.json();
            applyCSVData(result);
        } catch {
            toast.error("CSV2 upload error");
        }
    };

    /* ================= APPLY CSV DATA (COMMON) ================= */
    const applyCSVData = (result) => {
        if (result?.status !== "1") {
            toast.error(result?.msg || "CSV upload failed");
            return;
        }

        const d = result.data || {};

        setFormData((prev) => {
            const updated = {
                ...prev,
                impressions: toInt(d.impressions ?? prev.impressions),
                click: toInt(d.clicks ?? prev.click),
                revenue: d.revenue ? d.revenue.replace("$", "") : prev.revenue,
                fill_rate: d.match_rate
                    ? d.match_rate.replace("%", "")
                    : prev.fill_rate,
                ctr: d.ctr ? d.ctr.replace("%", "") : prev.ctr,
                cpm: d.ecpm ? d.ecpm.replace("$", "") : prev.cpm,
                viewability: d.show_rate
                    ? d.show_rate.replace("%", "") : d.viewability ? d.viewability.replace("%", "")
                        : prev.viewability,
            };

            setBaseData({
                impressions: updated.impressions,
                click: updated.click,
                revenue: updated.revenue,
            });

            return updated;
        });

        toast.success("CSV data applied successfully");
    };

    /* ================= INPUT CHANGE ================= */
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (["impressions", "click", "viewable_imps"].includes(name)) {
            if (!/^\d*$/.test(value)) return;
        } else {
            if (!/^\d*\.?\d{0,2}$/.test(value)) return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));

        if (["impressions", "click", "revenue"].includes(name)) {
            setBaseData((prev) => ({ ...prev, [name]: value }));
        }
    };

    /* ================= APPLY % ================= */
    const applyPercentage = () => {
        if (!percentage) return;

        const percent = Number(percentage) / 100;

        setFormData((prev) => ({
            ...prev,
            impressions: baseData.impressions
                ? Math.floor(baseData.impressions * percent)
                : prev.impressions,
            click: baseData.click
                ? Math.floor(baseData.click * percent)
                : prev.click,
            revenue: baseData.revenue
                ? (baseData.revenue * percent).toFixed(2)
                : prev.revenue,
        }));
    };

    /* ================= SUBMIT ================= */
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const res = await ApiHandler(
                "/add_revenue.php",
                "POST",
                {
                    date: moment(date).format("YYYY-MM-DD"),
                    user_id: userId,
                    domain_id: domainId,
                    ...formData,
                },
                userToken,
                dispatch,
                navigate
            );

            if (res?.data?.status === "1") {
                toast.success(res.data.msg);
                navigate(-1);
            } else {
                toast.error(res.data.msg);
            }
        } catch {
            toast.error("Submit failed");
        }
    };

    if (loading) return <Loading />;

    return (
        <div className="min-h-screen bg-gray-100 p-6">
            <div className="max-w-4xl mx-auto bg-white p-6 rounded-xl shadow-lg">

                {/* HEADER */}
                <div className="flex justify-between mb-4 gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded"
                    >
                        <FaArrowLeft /> Back
                    </button>

                    <div className="flex gap-2">
                        <label className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded cursor-pointer">
                            <FaUpload /> Upload CSV
                            <input type="file" accept=".csv" hidden onChange={handleCSVUpload} />
                        </label>

                        <label className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded cursor-pointer">
                            <FaUpload /> Upload CSV2
                            <input type="file" accept=".csv" hidden onChange={handleCSVUpload2} />
                        </label>
                    </div>
                </div>

                {/* DATE */}
                <DatePicker
                    selected={date}
                    onChange={(d) => {
                        setDate(d);
                    }}
                    dateFormat="yyyy-MM-dd"
                    className="w-full border rounded px-3 py-2 mb-4"
                />

                {/* FORM */}
                <form className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries({
                        impressions: "Impressions",
                        click: "Clicks",
                        revenue: "Revenue",
                        fill_rate: "Match Rate",
                        ctr: "CTR",
                        cpm: "CPM",
                        viewability: "Show Rate",
                        viewable_imps: "Viewable Impressions",
                    }).map(([key, label]) => (
                        <div key={key}>
                            <label className="block mb-1 font-medium">{label}</label>
                            <input
                                name={key}
                                value={formData[key]}
                                onChange={handleChange}
                                className="w-full border rounded px-3 py-2"
                            />
                        </div>
                    ))}
                </form>

                {/* % */}
                <div className="flex gap-3 mt-6">
                    <input
                        value={percentage}
                        onChange={(e) =>
                            /^\d{0,3}$/.test(e.target.value) &&
                            setPercentage(e.target.value)
                        }
                        placeholder="Enter %"
                        className="border rounded px-3 py-2 w-40"
                    />
                    <button
                        type="button"
                        onClick={applyPercentage}
                        className="bg-orange-600 text-white px-4 py-2 rounded"
                    >
                        <FaPercentage />
                    </button>
                </div>

                {/* SUBMIT */}
                <div className="mt-6 text-right">
                    <button
                        onClick={handleSubmit}
                        className="bg-orange-600 text-white px-6 py-2 rounded"
                    >
                        Submit
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AddRevenuePage;
